import { LegalShell } from "@/components/app/LegalShell";

const SECTION_H = "text-lg font-display font-semibold tracking-tight mt-8 mb-2";

export default function RefundPolicyPage() {
    return (
        <LegalShell
            overline="Legal"
            title="Refund Policy"
            lastUpdated="February 2026"
            testid="refund-page"
        >
            <p>
                We want FormFlo customers to feel they can trial the product risk-free. This Refund Policy explains when and how
                you can get money back from <strong>Rishmathi Tech Ventures Pvt Ltd</strong>.
            </p>

            <h2 className={SECTION_H}>1. Cooling-off period (first 7 days)</h2>
            <p>
                If you cancel within <strong>7 days</strong> of your first paid subscription (the &ldquo;cooling-off window&rdquo;), you are entitled to a
                <strong> full refund</strong> of the amount paid for that subscription, no questions asked.
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>The cooling-off window applies only to your <em>first</em> annual subscription, not to renewals or expansion-pack top-ups.</li>
                <li>Bank fees / payment gateway charges (if any) are non-refundable but Zoho Payments does not levy these for standard methods.</li>
            </ul>

            <h2 className={SECTION_H}>2. Pro-rata refund (after the cooling-off window)</h2>
            <p>
                After the 7-day window, if at any point during a paid annual term you are no longer happy with the Service, you may request a
                <strong> pro-rata refund for the remaining whole months</strong> of your subscription. The calculation is:
            </p>
            <pre className="font-mono text-xs bg-secondary border border-border rounded-sm p-3 overflow-x-auto">
{`refund = (annual_fee_paid / 12) × full_months_remaining`}
            </pre>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>&ldquo;Full months remaining&rdquo; is counted from the calendar date of your written cancellation request, rounded <em>down</em> to whole months.</li>
                <li>Once a refund is processed, your access is downgraded to read-only for 30 days and then permanently removed (see Privacy Policy §6).</li>
                <li>Expansion packs purchased mid-term are refunded under the same pro-rata rule, anchored to that pack&apos;s own purchase date.</li>
            </ul>

            <h2 className={SECTION_H}>3. Worked example</h2>
            <p className="bg-secondary/40 border border-border rounded-sm p-4 text-sm">
                You paid <strong>₹5,988</strong> for an annual plan on <em>1 January</em>. On <em>15 May</em> you decide to cancel.<br />
                Full months remaining after the cancellation date = <strong>7</strong> (June, July, … December).<br />
                Refund = <strong>(5,988 / 12) × 7 = ₹3,493</strong>, credited back to the original payment method within 7–10 business days.
            </p>

            <h2 className={SECTION_H}>4. Non-refundable situations</h2>
            <p>Refunds will not be granted in the following cases:</p>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>The organization has been terminated by us for material breach (prohibited content, security abuse, fraud, repeated FUP violations).</li>
                <li>The remaining subscription period is less than one full month.</li>
                <li>The customer is requesting a refund of a renewal that was previously billed and used.</li>
                <li>Add-on / professional services purchased separately (e.g. custom integrations) where work has already begun.</li>
            </ul>

            <h2 className={SECTION_H}>5. How to request a refund</h2>
            <ol className="list-decimal pl-5 space-y-1.5">
                <li>Email <a href="mailto:contact@rishmathi.com" className="text-primary hover:underline">contact@rishmathi.com</a> from the Org Admin&apos;s registered email address.</li>
                <li>Subject: <code className="font-mono text-xs">Refund request — &lt;organization-name&gt;</code>.</li>
                <li>Include the reason for cancellation (so we can improve), the original order ID or Zoho payment ID, and the date you would like the cancellation to take effect.</li>
                <li>We will acknowledge within <strong>2 business days</strong> and process the refund (if eligible) within <strong>7–10 business days</strong> to the original payment method.</li>
            </ol>

            <h2 className={SECTION_H}>6. Disputes</h2>
            <p>
                If you believe a refund decision is incorrect, you may escalate to <a href="mailto:contact@rishmathi.com" className="text-primary hover:underline">contact@rishmathi.com</a> within 15 days of our response. Unresolved disputes are subject to the
                governing law &amp; jurisdiction clause of the Terms (Hyderabad, Telangana, India).
            </p>

            <h2 className={SECTION_H}>7. Contact</h2>
            <p>
                Rishmathi Tech Ventures Pvt Ltd · Hyderabad, Telangana, India ·{" "}
                <a href="mailto:contact@rishmathi.com" className="text-primary hover:underline">contact@rishmathi.com</a>
            </p>
        </LegalShell>
    );
}
